// Libraries
import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { Mail02, Plus } from "@untitled-ui/icons-react";
import { Switch } from "@lockerpm/design";
import { apiErrorHandler } from "#src/utils/apiErrorHandler";
import store from "#src/store";

// Components
import ConfigSectionWrapperV2 from "#src/commonV2/helper/wrapper/ConfigSection";
import { DeleteDomainModal } from "./DeleteDomainModal";
import { UseEmailFromDomainModal } from "./UseEmailFromDomainModal";
import { NotUseEmailFromDomainModal } from "./NotUseEmailFromDomainModal";
import { UpdateDomainsModal } from "./UpdateDomainsModal";
import { createNotification } from "#src/common/system/notifications";
import { updateCurrentWorkspaceV2 } from "#src/store/slices/auth";

// API-related
import workspaceServices, {
  type IWorkspaceItem,
  type IEnabledEnterpriseEmail,
} from "#src/services/workspace";

interface IUseEmailFromDomainProps {
  workspaceId: string;
  allowedToUpdate: boolean;
  currentWorkspace: IWorkspaceItem;
  setCurrentWorkspace: Dispatch<SetStateAction<IWorkspaceItem>>;
}

const UseEmailFromDomain = ({
  workspaceId,
  allowedToUpdate,
  currentWorkspace,
  setCurrentWorkspace
}: IUseEmailFromDomainProps) => {
  const { t } = useTranslation("workspace", { keyPrefix: "security" });

  const [newDomains, setNewDomains] = useState<string[]>([])
  const [openDeleteDomainModal, setOpenDeleteDomainModal] = useState<boolean>(false);
  const [openUseEmailFromDomainModal, setOpenUseEmailFromDomainModal] = useState<boolean>(false);
  const [openNotUseEmailFromDomainModal, setOpenNotUseEmailFromDomainModal] = useState<boolean>(false);
  const [openUpdateDomainsModal, setOpenUpdateDomainsModal] = useState<boolean>(false);

  const [callingAPI, setCallingAPI] = useState(false);

  const domains = useMemo(() => {
    return currentWorkspace.enterprise_email_domains || []
  }, [currentWorkspace.enterprise_email_domains])

  const deletedDomains = useMemo(() => {
    return domains.filter((d) => !newDomains.includes(d))
  }, [domains, newDomains])

  useEffect(() => {
    setNewDomains(domains)
  }, [domains]);

  const enabledEnterpriseEmail = async (data: IEnabledEnterpriseEmail, callback?: () => void) => {
    setCallingAPI(true);
    workspaceServices.enabled_enterprise_email(workspaceId, data).then(() => {
      setCurrentWorkspace({
        ...currentWorkspace,
        ...data,
      })
      store.dispatch(updateCurrentWorkspaceV2({
        ...currentWorkspace,
        ...data,
      }))
      if (callback) {
        callback();
      }
    }).catch((error) => {
      apiErrorHandler(error, {
        toastMessage: t("notification.enabledEnterpriseEmail.fail"),
      });
    }).finally(() => {
      setCallingAPI(false);
    })
  }

  return (
    <>
      <ConfigSectionWrapperV2
        icon={<Mail02 className="h-5 w-5 text-gray-v2-500" />}
        headerTitle={t("useEmailFromDomain.title")}
      >
        <div className="flex flex-col gap-8 p-4">
          <div className="flex gap-5 justify-between">
            <p className="text-md font-regular text-gray-v2-900">{t("useEmailFromDomain.description")}</p>
            <Switch
              disabled={!allowedToUpdate}
              checked={currentWorkspace.enterprise_email_enabled}
              onChange={() => {
                if (!currentWorkspace.enterprise_email_enabled) {
                  if (domains.length > 0) {
                    setOpenUseEmailFromDomainModal(true);
                  } else {
                    createNotification({
                      type: "error",
                      message: t("useEmailFromDomain.alert.message"),
                      detail: t("useEmailFromDomain.alert.detail"),
                    });
                  }
                } else {
                  setOpenNotUseEmailFromDomainModal(true);
                }
              }}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <p className="text-gray-v2-700 text-sm font-medium">
              {t("useEmailFromDomain.allowedDomains")}
            </p>
            <div className="flex gap-1.5 wrap">
              {
                domains.map((d, index) => (<div
                  key={index}
                  className="h-7 px-2 bg-gray-v2-50 border border-gray-v2-200 rounded-xl flex items-center"
                >
                  <span className="text-sm font-medium text-gray-v2-700">
                    {d}
                  </span>
                </div>))
              }
              <div
                className={`h-7 w-7 bg-gray-v2-50 border border-gray-v2-200 flex items-center justify-center rounded-full ${allowedToUpdate ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                onClick={() => {
                  if (allowedToUpdate) {
                    setNewDomains(domains);
                    setOpenUpdateDomainsModal(true);
                  }
                }}
              >
                <Plus className={`w-4 h-4 ${allowedToUpdate ? 'text-gray-v2-500' : 'text-gray-v2-200'}`}/>
              </div>
            </div>
          </div>
        </div>
      </ConfigSectionWrapperV2>
      <UseEmailFromDomainModal
        open={openUseEmailFromDomainModal}
        callingAPI={callingAPI}
        onClose={() => {
          setOpenUseEmailFromDomainModal(false)
        }}
        onConfirm={() => {
          enabledEnterpriseEmail({
            enterprise_email_enabled: true,
            enterprise_email_domains: domains
          }, () => {
            setOpenUseEmailFromDomainModal(false)
          })
        }}
      />
      <NotUseEmailFromDomainModal
        open={openNotUseEmailFromDomainModal}
        callingAPI={callingAPI}
        onClose={() => {
          setOpenNotUseEmailFromDomainModal(false)
        }}
        onConfirm={() => {
          enabledEnterpriseEmail({
            enterprise_email_enabled: false,
            enterprise_email_domains: domains
          }, () => {
            setOpenNotUseEmailFromDomainModal(false)
          })
        }}
      />
      <UpdateDomainsModal
        open={openUpdateDomainsModal}
        callingAPI={callingAPI}
        newDomains={newDomains}
        setNewDomains={setNewDomains}
        onClose={() => {
          setOpenUpdateDomainsModal(false);
        }}
        onSave={() => {
          if (deletedDomains.length > 0 && currentWorkspace.enterprise_email_enabled) {
            setOpenUpdateDomainsModal(false);
            setOpenDeleteDomainModal(true);
          } else {
            enabledEnterpriseEmail({
              enterprise_email_enabled: currentWorkspace.enterprise_email_enabled,
              enterprise_email_domains: newDomains
            }, () => {
              setOpenUpdateDomainsModal(false)
            })
          }
        }}
      />
      <DeleteDomainModal
        open={openDeleteDomainModal}
        callingAPI={callingAPI}
        deletedDomains={deletedDomains}
        onClose={() => {
          setOpenDeleteDomainModal(false);
          setOpenUpdateDomainsModal(true);
        }}
        deleteDomain={() => {
          enabledEnterpriseEmail({
            enterprise_email_enabled: currentWorkspace.enterprise_email_enabled,
            enterprise_email_domains: newDomains
          }, () => {
            setOpenDeleteDomainModal(false);
            setOpenUpdateDomainsModal(false);
          })
        }}
      />
    </>
  );
};

export default UseEmailFromDomain;
  